/** My notification choices for one project.
 *
 * Two independent choices — the digest level and the events toggle — so digest-only,
 * events-only, both, or neither are all ordinary states. Changes apply immediately;
 * "Email me it now" exists so nobody waits for Monday to see what a digest looks like.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Button, ErrorState, Field, SectionHeader, inputClass } from '@/components/ui'
import { api } from '@/lib/api/client'

interface Subscription {
  subscribed: boolean
  digest: 'none' | 'executive' | 'detailed'
  eventsEnabled: boolean
}

const subscriptionKey = (projectId: string) => ['projects', projectId, 'subscription'] as const

function useSubscription(projectId: string) {
  return useQuery({
    queryKey: subscriptionKey(projectId),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/projects/{project_id}/subscription', {
        params: { path: { project_id: projectId } },
      })
      return data as unknown as Subscription
    },
  })
}

export function NotificationSettings({ projectId }: { projectId: string }) {
  const client = useQueryClient()
  const { data: subscription } = useSubscription(projectId)

  const save = useMutation({
    mutationFn: async (body: { digest: string; events_enabled: boolean }) => {
      const { data } = await api.PUT('/api/v1/projects/{project_id}/subscription', {
        params: { path: { project_id: projectId } },
        body: body as never,
      })
      return data as unknown as Subscription
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: subscriptionKey(projectId) }),
  })

  const sendNow = useMutation({
    mutationFn: async (level: string) => {
      const { data } = await api.POST('/api/v1/projects/{project_id}/subscription/send-now', {
        params: { path: { project_id: projectId } },
        body: { level } as never,
      })
      return data as unknown as { sentTo: string }
    },
  })

  if (!subscription) return null

  const digest = subscription.digest
  const events = subscription.eventsEnabled

  return (
    <section>
      <SectionHeader>Notifications</SectionHeader>
      <div className="card space-y-3 p-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Weekly digest" hint="Lands Monday morning.">
            <select
              className={inputClass}
              value={digest}
              onChange={(event) =>
                save.mutate({ digest: event.target.value, events_enabled: events })
              }
            >
              <option value="none">No digest</option>
              <option value="executive">Executive overview</option>
              <option value="detailed">Detailed</option>
            </select>
          </Field>
          <Field label="Event notices" hint="Health, target date, stage, blocked work.">
            <select
              className={inputClass}
              value={events ? 'on' : 'off'}
              onChange={(event) =>
                save.mutate({ digest, events_enabled: event.target.value === 'on' })
              }
            >
              <option value="off">Off</option>
              <option value="on">On — email me when it happens</option>
            </select>
          </Field>
        </div>

        <p className="text-xs text-fg-muted">
          {subscription.subscribed
            ? 'Subscribed. No digest and no events means unsubscribed.'
            : 'Not subscribed. Pick a digest, events, or both.'}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() => sendNow.mutate(digest === 'none' ? 'executive' : digest)}
            disabled={sendNow.isPending}
          >
            {sendNow.isPending ? 'Sending…' : 'Email me this digest now'}
          </Button>
          {sendNow.data ? (
            <span className="text-xs text-fg-muted">Sent to {sendNow.data.sentTo}.</span>
          ) : null}
        </div>

        {save.error ? <ErrorState error={save.error} /> : null}
        {sendNow.error ? <ErrorState error={sendNow.error} /> : null}
      </div>
    </section>
  )
}
